import { EnumDropdownItemVariant, type IDropdownItem } from 'shared/ui'
import { truthy } from 'shared/lib'
import { type IInboxCombine } from 'entities/Inbox'
import { PhoneRenderContent } from 'entities/Phone'
import { Inbox } from 'entities/Inbox/model/Inbox'
import { type Phone } from 'entities/Phone/model/Phone'
import { type ISendFromStoreItem } from 'widgets/SendFrom/store/type'
import { getSendingOptionItems } from './getSendingOptionItems'
import { getShortCodeDropdownItem } from './getShortCodeDropdownItem'

export type IGetDropdownItemsOptions = {
  excludeNumbersIds?: number[]
  excludeShortCode?: boolean
  disabledClosedTooltip?: true
  excludeAircall?: true
  showOnlyVerifiedToolFree?: true
  excludeSIPTrunk?: true
  excludeSendingOptions?: true
  withGropingChild?: true
  hideChildren?: boolean
  getInboxDropdownItemProps?: (inbox: Inbox) => Partial<IDropdownItem>
}

export const getDropdownItems = (
  inboxes: IInboxCombine[],
  options?: IGetDropdownItemsOptions
): IDropdownItem<ISendFromStoreItem>[] => {
  const disabledClosedTooltip = options?.disabledClosedTooltip
  return inboxes
    .sort((inbox) => (inbox instanceof Inbox && inbox.isShortCode ? -1 : 0))
    .map((inbox): IDropdownItem<ISendFromStoreItem> | null => {
      if (inbox instanceof Inbox) {
        if (options?.excludeSIPTrunk && inbox.call_via_aircall?.is_active) return null
        if (options?.excludeShortCode && inbox.isShortCode) return null

        const shouldIncludeNumber = (number: Phone): boolean => {
          if (
            'id' in number &&
            options?.excludeNumbersIds &&
            options?.excludeNumbersIds.includes(number.id)
          )
            return false
          if (options?.excludeAircall && number.is_aircall) return false
          if (options?.excludeShortCode && number.isShortCode) return false
          if (options?.showOnlyVerifiedToolFree && number.isTollFree && !number.isStatusVerified)
            return false
          return true
        }

        const children: IDropdownItem<ISendFromStoreItem>[] = options?.excludeSendingOptions
          ? []
          : [...getSendingOptionItems(inbox, { disabledClosedTooltip })]
        const correctedNumbers = !options?.hideChildren ? inbox.numbers : inbox.numbers.slice(0, 1)
        correctedNumbers.forEach((number) => {
          if (!shouldIncludeNumber(number)) return
          const getNumberGroup = () => {
            if (number.isLocal) {
              return 'Local numbers'
            }
            if (number.isTollFree) {
              return 'Toll-free numbers'
            }
            if (number.isLandline) {
              return 'Landline numbers'
            }
          }
          children.push({
            id: `${inbox.id}_${number.id}`,
            disabledClosedTooltip,
            group: getNumberGroup(),
            searchPattern: `${number.number}${number.formatted_number}${number.national_number}`,
            data: {
              inbox,
              number,
              parentLabel: inbox.name,
              parentIcon: inbox.icon,
            },
            label: number?.formatted_number,
            labelContent: <PhoneRenderContent phone={number} />,
            variant: EnumDropdownItemVariant.CheckedLeft,
            noRenderEmptyActiveValue: true,
          })
        })

        if (inbox.isShortCode)
          return getShortCodeDropdownItem(inbox, !options?.hideChildren ? children : undefined, {
            disabledClosedTooltip,
          })

        if (!children.length && !inbox.isRCS) return null

        return {
          id: inbox.id,
          disabledClosedTooltip,
          searchPattern: `${inbox.name}${children.reduce(
            (str, item) => str + item.searchPattern,
            ''
          )}`,
          group: 'Inboxes',
          label: inbox.name,
          ariaLabel: 'SelectInbox',
          children: options?.hideChildren ? undefined : children,
          data:
            children.length === 1
              ? children[0].data
              : {
                  inbox,
                },
          iconL: inbox.icon,
          isCardClick: children?.length <= 1,
          iconR: options?.hideChildren ? undefined : 'chevronRight',
          childrenDropdownProps: {
            margin: 0,
            marginTop: -3,
            width: !!inbox.sendingOptions ? 256 : 162,
            TransitionProps: {
              timeout: 100,
            },
          },
          variant: EnumDropdownItemVariant.Default,
          ...options?.getInboxDropdownItemProps?.(inbox),
        }
      }
      return null
    })
    .filter(truthy)
}
